import React, { useMemo, useState } from 'react';
import { PageLayout } from '../shared/PageLayout';
import { StatCard } from '../shared/StatCard';
import { DataTable, Column } from '../shared/DataTable';
import { MasteryBar } from '../shared/MasteryBar';
import { Badge } from '../../../ui/Badge';
import { Card } from '../../../ui/Card';
import { Button } from '../../../ui/Button';
import { CheckCircle2Icon, XCircleIcon, SearchIcon, FilterIcon, BookOpenIcon, HelpCircleIcon, FileTextIcon, XIcon } from 'lucide-react';
import { TAXONOMY } from '../../../../config/taxonomyRegistry';
import { resolveUnifiedSupport } from '../../../../support/supportResolver';
import { ResolvedSupport } from '../../../../support/types';
interface FamilySupportRow {
  id: string;
  familyId: string;
  module: string;
  support: ResolvedSupport;
  completeness: number;
}
export function SupportContentPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [coverageFilter, setCoverageFilter] = useState<'all' | 'full' | 'partial' | 'none'>('all');
  const [selectedFamily, setSelectedFamily] = useState<FamilySupportRow | null>(null);
  // Generate data
  const allData: FamilySupportRow[] = useMemo(() => {
    return TAXONOMY.map(family => {
      const support = resolveUnifiedSupport(family.familyId);
      return {
        id: family.familyId,
        familyId: family.familyId,
        module: family.module,
        support,
        completeness: support.coverage.completeness
      };
    });
  }, []);
  // Calculate stats
  const stats = useMemo(() => {
    const total = allData.length;
    const full = allData.filter(d => d.completeness === 100).length;
    const partial = allData.filter(d => d.completeness > 0 && d.completeness < 100).length;
    const none = allData.filter(d => d.completeness === 0).length;
    return {
      total,
      full,
      partial,
      none
    };
  }, [allData]);
  // Filter data
  const filteredData = useMemo(() => {
    return allData.filter(row => {
      // Search
      if (searchQuery && !row.familyId.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Module
      if (moduleFilter !== 'all' && row.module !== moduleFilter) {
        return false;
      }
      // Coverage
      if (coverageFilter === 'full' && row.completeness !== 100) return false;
      if (coverageFilter === 'partial' && (row.completeness === 0 || row.completeness === 100)) return false;
      if (coverageFilter === 'none' && row.completeness !== 0) return false;
      return true;
    });
  }, [allData, searchQuery, moduleFilter, coverageFilter]);
  const modules = useMemo(() => {
    return Array.from(new Set(TAXONOMY.map(f => f.module))).sort();
  }, []);
  const renderCheck = (hasItem: boolean) => {
    return hasItem ? <CheckCircle2Icon className="w-4 h-4 text-emerald-500" data-id="element-388" /> : <XCircleIcon className="w-4 h-4 text-gray-300" data-id="element-389" />;
  };
  const columns: Column<FamilySupportRow>[] = [{
    key: 'familyId',
    label: 'Family ID',
    render: val => <span className="font-mono text-xs font-medium text-gray-700" data-id="element-390">
          {val}
        </span>
  }, {
    key: 'module',
    label: 'Domein',
    render: val => <Badge variant="secondary" data-id="element-391">{val}</Badge>
  }, {
    key: 'explanation',
    label: 'Uitleg',
    render: (_, row) => renderCheck(row.support.coverage.hasExplanation)
  }, {
    key: 'cheatSheet',
    label: 'Spiekbriefje',
    render: (_, row) => renderCheck(row.support.coverage.hasCheatSheet)
  }, {
    key: 'hints',
    label: 'Hints',
    render: (_, row) => renderCheck(row.support.coverage.hasHints)
  }, {
    key: 'steps',
    label: 'Stappen',
    render: (_, row) => renderCheck(row.support.coverage.hasSteps)
  }, {
    key: 'afterSummary',
    label: 'Samenvatting',
    render: (_, row) => renderCheck(row.support.coverage.hasAfterSummary)
  }, {
    key: 'completeness',
    label: 'Compleetheid',
    render: val => <MasteryBar value={val} data-id="element-392" />
  }];
  return <PageLayout title="Ondersteuning Content" description="Beheer en controleer de uitleg, hints en spiekbriefjes per family." data-id="element-393">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" data-id="element-394">
        <StatCard label="Totaal Families" value={stats.total} icon={BookOpenIcon} color="blue" data-id="element-395" />
        <StatCard label="Volledig (100%)" value={stats.full} icon={CheckCircle2Icon} color="emerald" data-id="element-396" />
        <StatCard label="Gedeeltelijk (1-99%)" value={stats.partial} icon={HelpCircleIcon} color="amber" data-id="element-397" />
        <StatCard label="Geen (0%)" value={stats.none} icon={XCircleIcon} color="red" data-id="element-398" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6 bg-white p-4 rounded-xl border border-gray-200" data-id="element-399">
        <div className="relative flex-1" data-id="element-400">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-401" />
          <input type="text" placeholder="Zoek op Family ID..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-402" />
        </div>
        <div className="flex items-center gap-2" data-id="element-403">
          <FilterIcon className="w-4 h-4 text-gray-400" data-id="element-404" />
          <select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-405">
            <option value="all" data-id="element-406">Alle domeinen</option>
            {modules.map(m => <option key={m} value={m} data-id="element-407">
                {m}
              </option>)}
          </select>
          <select value={coverageFilter} onChange={e => setCoverageFilter(e.target.value as any)} className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-408">
            <option value="all" data-id="element-409">Alle dekkingen</option>
            <option value="full" data-id="element-410">Volledig (100%)</option>
            <option value="partial" data-id="element-411">Gedeeltelijk (1-99%)</option>
            <option value="none" data-id="element-412">Geen (0%)</option>
          </select>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6" data-id="element-413">
        {/* Table */}
        <div className={`flex-1 transition-all ${selectedFamily ? 'lg:w-2/3' : 'w-full'}`} data-id="element-414">
          <DataTable columns={columns} data={filteredData} onRowClick={setSelectedFamily} emptyMessage="Geen families gevonden met deze filters." data-id="element-415" />
        </div>

        {/* Preview Panel */}
        {selectedFamily && <div className="w-full lg:w-1/3 flex-shrink-0" data-id="element-416">
            <Card className="sticky top-6 flex flex-col max-h-[calc(100vh-120px)]" data-id="element-417">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50 rounded-t-xl" data-id="element-418">
                <div data-id="element-419">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-420">
                    <FileTextIcon className="w-4 h-4 text-blue-500" data-id="element-421" />
                    Content Preview
                  </h3>
                  <p className="text-xs font-mono text-gray-500 mt-1" data-id="element-422">
                    {selectedFamily.familyId}
                  </p>
                </div>
                <Button variant="secondary" onClick={() => setSelectedFamily(null)} className="p-1.5 h-auto" data-id="element-423">
                  <XIcon className="w-4 h-4" data-id="element-424" />
                </Button>
              </div>

              <div className="p-4 overflow-y-auto flex-1 space-y-6" data-id="element-425">
                {/* Explanation */}
                <div data-id="element-426">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-427">
                    {renderCheck(selectedFamily.support.coverage.hasExplanation)}
                    Uitleg (Zo werkt het)
                  </h4>
                  {selectedFamily.support.explanation ? <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100" data-id="element-428">
                      <p className="text-sm font-medium text-blue-900 mb-1" data-id="element-429">
                        {selectedFamily.support.explanation.title}
                      </p>
                      <p className="text-sm text-blue-800 whitespace-pre-wrap" data-id="element-430">
                        {selectedFamily.support.explanation.body}
                      </p>
                    </div> : <p className="text-sm text-gray-400 italic" data-id="element-431">
                      Geen uitleg beschikbaar.
                    </p>}
                </div>

                {/* Steps */}
                <div data-id="element-432">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-433">
                    {renderCheck(selectedFamily.support.coverage.hasSteps)}
                    Stappenplan
                  </h4>
                  {selectedFamily.support.steps.length > 0 ? <ol className="list-decimal list-inside space-y-1" data-id="element-434">
                      {selectedFamily.support.steps.map((step, i) => <li key={i} className="text-sm text-gray-700" data-id="element-435">
                          {step}
                        </li>)}
                    </ol> : <p className="text-sm text-gray-400 italic" data-id="element-436">
                      Geen stappenplan beschikbaar.
                    </p>}
                </div>

                {/* Hints */}
                <div data-id="element-437">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-438">
                    {renderCheck(selectedFamily.support.coverage.hasHints)}
                    Hints
                  </h4>
                  {selectedFamily.support.hints.length > 0 ? <div className="space-y-2" data-id="element-439">
                      {selectedFamily.support.hints.map((hint, i) => <div key={i} className="bg-amber-50 p-2 rounded border border-amber-100 text-sm text-amber-900" data-id="element-440">
                          <span className="font-medium mr-2" data-id="element-441">
                            Hint {i + 1}:
                          </span>
                          {hint}
                        </div>)}
                    </div> : <p className="text-sm text-gray-400 italic" data-id="element-442">
                      Geen hints beschikbaar.
                    </p>}
                </div>

                {/* Cheat Sheet */}
                <div data-id="element-443">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-444">
                    {renderCheck(selectedFamily.support.coverage.hasCheatSheet)}
                    Spiekbriefje
                  </h4>
                  {selectedFamily.support.cheatSheet ? <div className="bg-emerald-50/50 p-3 rounded-lg border border-emerald-100" data-id="element-445">
                      <p className="text-sm font-medium text-emerald-900 mb-1" data-id="element-446">
                        {selectedFamily.support.cheatSheet.title}
                      </p>
                      <p className="text-sm text-emerald-800 whitespace-pre-wrap" data-id="element-447">
                        {selectedFamily.support.cheatSheet.body}
                      </p>
                      {selectedFamily.support.cheatSheet.rules && selectedFamily.support.cheatSheet.rules.length > 0 && <ul className="list-disc list-inside mt-2 text-sm text-emerald-800" data-id="element-448">
                            {selectedFamily.support.cheatSheet.rules.map((rule, i) => <li key={i} data-id="element-449">{rule}</li>)}
                          </ul>}
                    </div> : <p className="text-sm text-gray-400 italic" data-id="element-450">
                      Geen spiekbriefje beschikbaar.
                    </p>}
                </div>

                {/* After Summary */}
                <div data-id="element-451">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2" data-id="element-452">
                    {renderCheck(selectedFamily.support.coverage.hasAfterSummary)}
                    Sessie Samenvatting
                  </h4>
                  {selectedFamily.support.afterSummary ? <div className="bg-purple-50 p-3 rounded-lg border border-purple-100" data-id="element-453">
                      <p className="text-sm text-purple-900" data-id="element-454">
                        {selectedFamily.support.afterSummary}
                      </p>
                    </div> : <p className="text-sm text-gray-400 italic" data-id="element-455">
                      Geen samenvatting beschikbaar.
                    </p>}
                </div>
              </div>
            </Card>
          </div>}
      </div>
    </PageLayout>;
}